import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { Subscription } from "rxjs";
import { AngularMaterialModule } from "../../angular-material.module";
import { AuthService } from "../../auth/auth.service";
import { PlayersService } from "../players.service";
import { RosterRole } from "../player.model";

interface RosterInvitationView {
  inviteType: "email" | "share_link";
  rosterRole: RosterRole;
  email: string | null;
  teamId: string | null;
  teamName: string;
  status: "pending" | "accepted" | "declined" | "expired" | "revoked";
  expiresAt: string;
  hasAccount: boolean;
  alreadyOnRoster?: boolean;
}

@Component({
  selector: "app-team-invite-accept",
  standalone: true,
  imports: [CommonModule, RouterModule, AngularMaterialModule],
  templateUrl: "./team-invite-accept.component.html",
  styleUrls: ["./team-invite-accept.component.css"],
})
export class TeamInviteAcceptComponent implements OnInit, OnDestroy {
  token = "";
  invitation: RosterInvitationView | null = null;
  isAuthenticated = false;
  isLoading = true;
  isAccepting = false;
  isDeclining = false;
  error: string | null = null;
  success: string | null = null;
  verificationRequiredAfterJoin = false;
  joinedTeamId: string | null = null;
  returnUrl = "";

  private authSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private playersService: PlayersService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get("token") || "";
    this.returnUrl = this.router.url;
    this.authSub = this.authService.getAuthStatusListener().subscribe((state) => {
      const becameAuthenticated = !this.isAuthenticated && state.isAuthenticated;
      this.isAuthenticated = state.isAuthenticated;
      if (becameAuthenticated && this.token) {
        this.loadInvitation();
      }
    });
    this.isAuthenticated = this.authService.getIsAuth();
    this.loadInvitation();
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
  }

  isShareLinkInvite(): boolean {
    return this.invitation?.inviteType === "share_link";
  }

  isStaffInvite(): boolean {
    return !!this.invitation && this.invitation.rosterRole !== "player";
  }

  get invitationRoleLabel(): string {
    if (this.invitation?.rosterRole === "general_manager") return "Manager";
    if (this.invitation?.rosterRole === "assistant_coach") return "Assistant Coach";
    if (this.invitation?.rosterRole === "coach") return "Coach";
    return "Player";
  }

  signupQueryParams(): Record<string, string> {
    return {
      intent: this.isStaffInvite() ? "other" : "player",
      returnUrl: this.returnUrl,
    };
  }

  loadInvitation(): void {
    if (!this.token) {
      this.error = "Invalid invitation link.";
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.error = null;
    this.playersService.getRosterInvitation(this.token).subscribe({
      next: (res) => {
        this.invitation = res.invitation;
        this.isLoading = false;
      },
      error: (err) => {
        this.error =
          err?.error?.message || err?.message || "Could not load invitation.";
        this.isLoading = false;
      },
    });
  }

  acceptInvitation(): void {
    if (!this.token || this.isAccepting) {
      return;
    }
    this.isAccepting = true;
    this.error = null;
    this.playersService.acceptRosterInvitation(this.token).subscribe({
      next: (res) => {
        this.isAccepting = false;
        this.success = res.message || "Invitation accepted.";
        this.verificationRequiredAfterJoin = res.identityVerificationRequired === true;
        this.joinedTeamId = res.teamId || this.invitation?.teamId || null;
        this.invitation = this.invitation
          ? { ...this.invitation, status: "accepted", alreadyOnRoster: true }
          : this.invitation;
        if (res.teamId && !this.verificationRequiredAfterJoin) {
          setTimeout(() => {
            void this.router.navigate(["/team", res.teamId, "roster"]);
          }, 1000);
        }
      },
      error: (err) => {
        this.isAccepting = false;
        if (err?.error?.code === "IDENTITY_VERIFICATION_REQUIRED") {
          void this.router.navigate(["/auth/player-verification"], {
            queryParams: { returnUrl: this.returnUrl },
          });
          return;
        }
        this.error =
          err?.error?.message || err?.message || "Could not accept invitation.";
      },
    });
  }

  verifyNow(): void {
    const returnUrl = this.joinedTeamId
      ? `/team/${this.joinedTeamId}/roster`
      : "/profile";
    void this.router.navigate(["/auth/player-verification"], {
      queryParams: { returnUrl },
    });
  }

  declineInvitation(): void {
    if (!this.token || this.isDeclining) {
      return;
    }
    this.isDeclining = true;
    this.error = null;
    this.playersService.declineRosterInvitation(this.token).subscribe({
      next: (res) => {
        this.isDeclining = false;
        this.success = res.message || "Invitation declined.";
        this.invitation = this.invitation
          ? { ...this.invitation, status: "declined" }
          : this.invitation;
      },
      error: (err) => {
        this.isDeclining = false;
        this.error =
          err?.error?.message || err?.message || "Could not decline invitation.";
      },
    });
  }
}
